import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import { addStateMaster, getCountriesForDropdown, updateStateMaster } from "../../../api/superAdminApi";

const emptyForm = () => ({
  country: null,
  state: "",
});

export default function AddStateModal({
  open,
  onClose,
  onSuccess,
  defaultCountry = null,
  initialData = null,
}) {
  const isEdit = Boolean(initialData?.id);
  const [form, setForm] = useState(emptyForm);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadCountries = useCallback(async () => {
    setLoadingCountries(true);
    try {
      const res = await getCountriesForDropdown();
      const list = res.data?.data;
      setCountries(Array.isArray(list) ? list : []);
    } catch {
      setCountries([]);
      toast.error("Failed to load countries.");
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadCountries();
    if (initialData?.id) {
      setForm({
        country: initialData.country
          ? {
              cid: initialData.country,
              country_name: initialData.country_name || "",
              country_code: initialData.country_code || "",
            }
          : defaultCountry || null,
        state: initialData.state || "",
      });
    } else {
      setForm({
        country: defaultCountry || null,
        state: "",
      });
    }
  }, [open, defaultCountry, initialData, loadCountries]);

  const canSave = Boolean(form.country?.cid && form.state.trim());

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    const payload = {
      state: form.state.trim(),
      country_id: form.country.cid,
    };
    try {
      const res = isEdit
        ? await updateStateMaster(initialData.id, payload)
        : await addStateMaster(payload);
      const body = res.data;
      if (!isEdit && body?.data?.alreadyExists) {
        toast.info(body.message || "State already exists");
      } else if (body?.status_code === 200) {
        toast.success(body.message || (isEdit ? "State updated successfully" : "State added successfully"));
        onSuccess?.();
        onClose();
      } else {
        toast.error(body?.message || (isEdit ? "Failed to update state." : "Failed to add state."));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || (isEdit ? "Failed to update state." : "Failed to add state."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Update State" : "Add State"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <Autocomplete
            options={countries}
            loading={loadingCountries}
            getOptionLabel={(o) => o.country_name || ""}
            isOptionEqualToValue={(a, b) => String(a?.cid) === String(b?.cid)}
            value={form.country}
            onChange={(_, value) => setForm((prev) => ({ ...prev, country: value }))}
            renderInput={(params) => (
              <TextField {...params} label="Country" required placeholder="Select country" />
            )}
          />
          <TextField
            label="State name"
            required
            fullWidth
            value={form.state}
            onChange={(e) => setForm((prev) => ({ ...prev, state: e.target.value }))}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isEdit ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
